import { isValidGln } from 'support/helpers/parties/gln';
import { PartnerDTO } from 'support/types';
import { FileInputProps } from 'components/Form/FileInput/FileInput';

export enum CSVRowKeys {
  TradePartnerName = 'Trade Partner Name',
  ContactPerson = 'Contact Person',
  Language = 'Language',
  EmailAddress = 'Email Address',
  Telephone = 'Telephone',
  InternalIdentifier = 'Internal Identifier',
  GLN = 'GLN',
}

export type IdentifierDomainType = PartnerDTO['preferredIdentifierDomain'];

export type CSVRow = {
  [CSVRowKeys.TradePartnerName]: string;
  [CSVRowKeys.ContactPerson]: string;
  [CSVRowKeys.Language]: string;
  [CSVRowKeys.EmailAddress]: string;
  [CSVRowKeys.Telephone]: string;
  [CSVRowKeys.InternalIdentifier]?: string;
  [CSVRowKeys.GLN]?: string;
};

/**
 * Possible error codes returned by the validateCSV function:
 * - 'invalid_csv': The CSV file is invalid or does not meet the required format.
 */
type ValidateCSVErrorCode = 'invalid_csv';

export type CSVIdentifierOptions = {
  identifierDomain: IdentifierDomainType;
  ownGlns: Set<string>;
};

export const createValidateCSV = (options: CSVIdentifierOptions): FileInputProps['validate'] => {
  return (results: Array<unknown> | unknown) => {
    const isWellFormed =
      Array.isArray(results) && results.length > 0 && results.every((row) => !!row && typeof row === 'object');

    if (isWellFormed && collectCSVValidationErrors(results, options).length === 0) {
      return { success: true };
    }
    return { success: false, errorCode: 'invalid_csv' as ValidateCSVErrorCode };
  };
};

export type CSVValidationErrorDetail = {
  row: number;
  column: string;
  value: string;
  expectedFormat: string;
};

export const collectCSVValidationErrors = (
  results: Array<unknown> | unknown,
  { identifierDomain, ownGlns }: CSVIdentifierOptions,
): Array<CSVValidationErrorDetail> => {
  const errorDetails: Array<CSVValidationErrorDetail> = [];

  if (!Array.isArray(results)) {
    return errorDetails;
  }

  const isBoth = hasBothDomains(identifierDomain);
  const isInternal = hasInternalDomain(identifierDomain);
  const glnCounts = countGlnValues(results, identifierDomain);

  results.forEach((row, index) => {
    const rowNumber = index + 1;

    if (!row || typeof row !== 'object') {
      return;
    }

    if (!hasValueOnRow(row, CSVRowKeys.TradePartnerName)) {
      errorDetails.push({
        row: rowNumber,
        column: CSVRowKeys.TradePartnerName,
        value: '',
        expectedFormat: 'required',
      });
    }

    if (!hasValueOnRow(row, CSVRowKeys.ContactPerson)) {
      errorDetails.push({
        row: rowNumber,
        column: CSVRowKeys.ContactPerson,
        value: '',
        expectedFormat: 'required',
      });
    }

    if (!hasValueOnRow(row, CSVRowKeys.Language)) {
      errorDetails.push({
        row: rowNumber,
        column: CSVRowKeys.Language,
        value: '',
        expectedFormat: 'required',
      });
    }

    if (!hasValueOnRow(row, CSVRowKeys.EmailAddress)) {
      errorDetails.push({
        row: rowNumber,
        column: CSVRowKeys.EmailAddress,
        value: '',
        expectedFormat: 'required',
      });
    }

    if (!hasValueOnRow(row, CSVRowKeys.Telephone)) {
      errorDetails.push({
        row: rowNumber,
        column: CSVRowKeys.Telephone,
        value: '',
        expectedFormat: 'required',
      });
    }

    if (isBoth) {
      const hasInternal = hasValueOnRow(row, CSVRowKeys.InternalIdentifier);
      const hasGLN = hasGlnValueOnRow(row);

      if (!hasInternal && !hasGLN) {
        errorDetails.push({
          row: rowNumber,
          column: `${CSVRowKeys.InternalIdentifier} / ${CSVRowKeys.GLN}`,
          value: '',
          expectedFormat: 'atLeastOneRequired',
        });
      } else if (hasInternal && hasGLN) {
        errorDetails.push({
          row: rowNumber,
          column: `${CSVRowKeys.InternalIdentifier} / ${CSVRowKeys.GLN}`,
          value: '',
          expectedFormat: 'onlyOne',
        });
      }
    } else if (isInternal && !hasValueOnRow(row, CSVRowKeys.InternalIdentifier)) {
      errorDetails.push({
        row: rowNumber,
        column: CSVRowKeys.InternalIdentifier,
        value: '',
        expectedFormat: 'required',
      });
    } else if (!isInternal && !hasGlnValueOnRow(row)) {
      errorDetails.push({
        row: rowNumber,
        column: CSVRowKeys.GLN,
        value: '',
        expectedFormat: 'required',
      });
    }

    const glnError = glnErrorForRow({ row, rowNumber, identifierDomain, ownGlns, glnCounts });
    if (glnError) {
      errorDetails.push(glnError);
    }
  });

  return errorDetails;
};

/**
 * GLN checks only apply to rows that resolve to the GS1 domain — the same per-row decision
 * `onLoadFile` makes. Rows carrying an Internal Identifier are free-form, so they yield null.
 *
 * Extracted from the row loop above to keep that callback under the `complexity` lint ceiling.
 */
const glnErrorForRow = ({
  row,
  rowNumber,
  identifierDomain,
  ownGlns,
  glnCounts,
}: {
  row: unknown;
  rowNumber: number;
  identifierDomain: IdentifierDomainType;
  ownGlns: Set<string>;
  glnCounts: Map<string, number>;
}): CSVValidationErrorDetail | null => {
  const gln = rowGlnValue(row, identifierDomain);

  if (!gln) {
    return null;
  }

  const base = { row: rowNumber, column: CSVRowKeys.GLN, value: gln };

  if (!isValidGln(gln)) {
    return { ...base, expectedFormat: 'invalidGln' };
  }

  if (ownGlns.has(gln)) {
    return { ...base, expectedFormat: 'ownGln' };
  }

  if ((glnCounts.get(gln) ?? 0) > 1) {
    return { ...base, expectedFormat: 'duplicateGln' };
  }

  return null;
};

const hasValueOnRow = (row: any, key: CSVRowKeys) => {
  return Object.hasOwn(row, key) && row[key] !== '';
};

const hasBothDomains = (identifierDomain: IdentifierDomainType) =>
  !!identifierDomain?.includes('internal') && !!identifierDomain?.includes('GS1');

const hasInternalDomain = (identifierDomain: IdentifierDomainType) => !!identifierDomain?.includes('internal');

/**
 * A GLN cell counts as filled only if it has non-whitespace content. The generic
 * `hasValueOnRow` treats '   ' as present, which would leave a whitespace-only GLN
 * passing both the required check and the GLN checks below.
 */
const hasGlnValueOnRow = (row: unknown): boolean => {
  if (!row || typeof row !== 'object') {
    return false;
  }
  return String((row as Record<string, unknown>)[CSVRowKeys.GLN] ?? '').trim() !== '';
};

/**
 * The trimmed GLN a row will actually submit, or null when the row resolves to the
 * internal domain. Mirrors the domain choice in NewBulkTradePartner.onLoadFile.
 */
const rowGlnValue = (row: unknown, identifierDomain: IdentifierDomainType): string | null => {
  if (!row || typeof row !== 'object') {
    return null;
  }
  const usesInternal = hasBothDomains(identifierDomain)
    ? hasValueOnRow(row, CSVRowKeys.InternalIdentifier)
    : hasInternalDomain(identifierDomain);

  if (usesInternal) {
    return null;
  }

  const value = String((row as Record<string, unknown>)[CSVRowKeys.GLN] ?? '').trim();
  return value === '' ? null : value;
};

const countGlnValues = (results: Array<unknown>, identifierDomain: IdentifierDomainType): Map<string, number> => {
  const counts = new Map<string, number>();
  results.forEach((row) => {
    const gln = rowGlnValue(row, identifierDomain);
    if (gln) {
      counts.set(gln, (counts.get(gln) ?? 0) + 1);
    }
  });
  return counts;
};
